import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import {
  ChevronUp,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  Heart,
  Loader2,
  MapPin,
  PackageCheck,
  Printer,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePrintPrivacy } from '@/hooks/usePrintPrivacy';
import { loadSelectedSales, type SelectedSaleDocument } from '@/lib/selectedSales';
import { buildPackingSlips } from '@/lib/packingSlips';
import { buildGreetingCards } from '@/lib/saleGreetings';
import { loadPrintSender } from '@/lib/printSenderSettings';
import { defaultTemplateSender, type TemplateSender } from '@/lib/printTemplates';
import { deliveryRpc, type DeliveryRow } from '@/lib/deliveries';
import { printHtmlContent } from '@/utils/printUtils';
import { customerPrintHtml } from '@/utils/customerPrintPrivacy';
import WorkModalShell from '@/components/common/WorkModalShell';
import AddressTemplateSheets from '@/components/printing/AddressTemplateSheets';
import SalesPackingSlips from '@/components/sales/SalesPackingSlips';
import SalesGreetingCards from '@/components/sales/SalesGreetingCards';
import { deliveryButton } from './DeliveryUpdateDialog';
import CourierHandoverDialog from './CourierHandoverDialog';
import { freezeHandoverSelection, type HandoverSelection } from '@/lib/courierHandover';

type Kind = 'pdf' | 'excel' | 'packing' | 'greetings' | 'address' | 'handover';
const documentActions = [
  ['pdf', 'PDF', FileText],
  ['excel', 'Excel', FileSpreadsheet],
  ['packing', 'Packing slips', PackageCheck],
  ['greetings', 'Greetings', Heart],
  ['address', 'Address labels', MapPin],
  ['handover', 'Courier handover', ClipboardList],
] as const;
export default function DeliveryDocuments({
  rows,
  compact = false,
}: {
  rows: DeliveryRow[];
  compact?: boolean;
}) {
  const { organization, can } = useAuth();
  const [working, setWorking] = useState<Kind | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [handover, setHandover] = useState<HandoverSelection[] | null>(null);
  const documentsTrigger = useRef<HTMLButtonElement>(null);
  const handoverTrigger = useRef<HTMLButtonElement>(null);
  const selectionIdentity = JSON.stringify(
    rows.map((row) => [row.sale_id, row.sale_updated_at, row.version]),
  );
  useEffect(() => {
    setHandover(null);
  }, [selectionIdentity]);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<{ kind: Kind; documents: SelectedSaleDocument[] } | null>(
    null,
  );
  const [sender, setSender] = useState<TemplateSender>(defaultTemplateSender);
  const [include, setInclude] = useState(false);
  const [shipments, setShipments] = useState<Record<string, DeliveryRow>>({});
  const [printing, setPrinting] = useState(false);
  const controller = useRef<AbortController | null>(null);
  const printController = useRef<AbortController | null>(null);
  const alive = useRef(true);
  const { askPrivacy, privacyDialog } = usePrintPrivacy();
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      controller.current?.abort();
      printController.current?.abort();
    };
  }, []);
  async function prepare(kind: Kind) {
    if (controller.current || !rows.length || !organization?.id || !can('sales.view')) return;
    if (kind === 'handover') {
      setError('');
      try {
        setHandover(freezeHandoverSelection(rows));
      } catch (failure) {
        setError(
          failure instanceof Error ? failure.message : 'The selection could not be prepared.',
        );
      }
      return;
    }
    const masked = kind === 'pdf' ? await askPrivacy() : false;
    if (masked === null || !alive.current || controller.current) return;
    const request = new AbortController();
    controller.current = request;
    setWorking(kind);
    setError('');
    try {
      const documents = await loadSelectedSales(
        rows.map((r) => ({ id: r.sale_id, sale_number: r.sale_number })),
        organization.id,
        request.signal,
        () => {},
      );
      if (request.signal.aborted) return;
      if (kind === 'address') {
        if (
          documents.some(
            (d) => (d.address?.length || 0) > 300 || (d.customerName?.length || 0) > 100,
          )
        )
          throw new Error(
            'An address is too long for these labels. Shorten it in the Sale or use a packing slip.',
          );
      }
      if (kind === 'address' || kind === 'packing') {
        const saved = await loadPrintSender(organization.id, request.signal);
        if (request.signal.aborted) return;
        setSender(saved?.sender || defaultTemplateSender());
        setInclude(!!saved);
      }
      if (kind === 'packing') {
        const current: Record<string, DeliveryRow> = {};
        for (let start = 0; start < rows.length; start += 4) {
          const chunk = await Promise.all(
            rows
              .slice(start, start + 4)
              .map((row) =>
                deliveryRpc<DeliveryRow>(
                  'get_sale_delivery',
                  { p_sale_id: row.sale_id },
                  request.signal,
                ),
              ),
          );
          for (const row of chunk) {
            const document = documents.find((d) => d.id === row.sale_id);
            if (!document || Date.parse(document.updatedAt) !== Date.parse(row.sale_updated_at))
              throw new Error(
                'A sale changed during preparation. Refresh and prepare the documents again.',
              );
            current[row.sale_id] = row;
          }
        }
        if (request.signal.aborted) return;
        setShipments(current);
      }
      if (kind === 'pdf' || kind === 'excel') {
        const exports = await import('@/utils/selectedSalesDocuments');
        if (request.signal.aborted) return;
        if (kind === 'pdf')
          exports
            .buildSelectedSalesPdf(documents, masked)
            .save(`${exports.selectedSalesFileName()}.pdf`);
        else exports.downloadSelectedSalesWorkbook(exports.buildSelectedSalesWorkbook(documents));
      } else setPreview({ kind, documents });
    } catch (e) {
      if (!request.signal.aborted)
        setError(e instanceof Error ? e.message : 'Documents could not be prepared.');
    } finally {
      if (controller.current === request) controller.current = null;
      if (alive.current) setWorking(null);
    }
  }
  async function printAddress() {
    const content = document.getElementById('address-template-sheets');
    if (
      content &&
      Array.from(content.querySelectorAll<HTMLElement>('.pt-from,.pt-to')).some(
        (section) => section.scrollHeight > section.clientHeight + 1,
      )
    ) {
      setError(
        'An address does not fit this label. Shorten its lines or use a packing slip before printing.',
      );
      return;
    }
    const masked = await askPrivacy();
    if (masked === null || !alive.current) return;
    const element = document.getElementById('address-template-sheets');
    if (!element || printController.current) return;
    const request = new AbortController();
    printController.current = request;
    printHtmlContent(customerPrintHtml(element, masked), {
      documentTitle: 'KLOTHIQ delivery address labels',
      signal: request.signal,
      onBeforePrint: () => setPrinting(true),
      onAfterPrint: () => {
        printController.current = null;
        if (alive.current) setPrinting(false);
      },
    });
  }
  return (
    <>
      {compact && (
        <Popover.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <Popover.Trigger asChild>
            <button
              ref={documentsTrigger}
              type="button"
              className={deliveryButton}
              disabled={!!working || !rows.length || !can('sales.view')}
              aria-label="Print and download selected deliveries"
            >
              {working ? (
                <Loader2
                  className="h-4 w-4 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              ) : (
                <Printer className="h-4 w-4" aria-hidden="true" />
              )}
              <span className="hidden sm:inline">{working ? 'Preparing…' : 'Documents'}</span>
              <span className="sm:hidden">{working ? 'Wait…' : 'Files'}</span>
              <ChevronUp className="hidden h-3.5 w-3.5 sm:block" aria-hidden="true" />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              side="top"
              align="end"
              sideOffset={12}
              collisionPadding={12}
              aria-label="Delivery documents"
              className="z-50 w-56 rounded-2xl border border-border bg-card p-2 shadow-2xl outline-none"
            >
              <p className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[.12em] text-muted-foreground">
                Print & download
              </p>
              {documentActions.map(([kind, label, Icon]) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    void prepare(kind);
                  }}
                  className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-sm outline-none hover:bg-muted focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
      {!compact && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-2">
          {documentActions.map(([kind, label, Icon]) => (
            <button
              ref={kind === 'handover' ? handoverTrigger : undefined}
              key={kind}
              className={deliveryButton}
              disabled={!!working || !rows.length || !can('sales.view')}
              onClick={() => void prepare(kind)}
            >
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              {working === kind ? 'Preparing…' : label}
            </button>
          ))}
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {error}
        </p>
      )}
      {preview?.kind === 'packing' && (
        <SalesPackingSlips
          slips={buildPackingSlips(preview.documents).map((slip) => ({
            ...slip,
            sender: include ? sender : undefined,
            shipment: shipments[slip.id]?.stage
              ? {
                  stage: shipments[slip.id]!.stage!,
                  courier: shipments[slip.id]!.courier_name,
                  tracking: shipments[slip.id]!.tracking_number,
                }
              : undefined,
          }))}
          onClose={() => setPreview(null)}
        />
      )}
      {preview?.kind === 'greetings' && (
        <SalesGreetingCards
          cards={buildGreetingCards(preview.documents)}
          onClose={() => setPreview(null)}
        />
      )}
      {preview?.kind === 'address' && (
        <WorkModalShell
          open
          wide
          title="Delivery address labels"
          busy={printing}
          onClose={() => setPreview(null)}
        >
          <header className="space-y-2 border-b border-border py-3">
            <h2 className="text-lg font-semibold">Delivery address labels</h2>
            <p className="text-sm text-muted-foreground">
              Three labels per A4 sheet. Missing details remain blank for handwriting.
            </p>
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={include}
                onChange={(e) => setInclude(e.target.checked)}
              />
              Include sender address shown below
            </label>
            <Link to="/print-templates" className="text-sm text-primary underline">
              Manage saved sender address
            </Link>
          </header>
          <div className="min-h-0 overflow-auto bg-muted/20 py-4">
            <AddressTemplateSheets
              copies={preview.documents.length}
              perPage={3}
              sender={sender}
              includeSender={include}
              recipients={preview.documents.map((d) => ({
                name: d.customerName || '',
                address: d.address || '',
                phone: d.phone || '',
                reference: d.number,
              }))}
            />
          </div>
          <footer className="flex justify-end gap-2 border-t border-border pt-3">
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <button disabled={printing} onClick={() => setPreview(null)} className={deliveryButton}>
              Close
            </button>
            <button
              disabled={printing}
              onClick={() => void printAddress()}
              className={deliveryButton}
            >
              {printing ? 'Printing…' : 'Print labels'}
            </button>
          </footer>
        </WorkModalShell>
      )}
      {handover && (
        <CourierHandoverDialog
          selection={handover}
          onClose={() => {
            setHandover(null);
            requestAnimationFrame(() =>
              (compact ? documentsTrigger : handoverTrigger).current?.focus(),
            );
          }}
        />
      )}
      {privacyDialog}
    </>
  );
}
